import { useConfigStore } from '../store/configStore'
import { useWorkspaceStore } from '../store/workspaceStore'
import { flushDirtyCommonModules } from './commonModuleFlush'
import { configObjectApi } from './configObjectApi'
import { configService } from './configService'
import { flushDesignerPropertyDrafts } from './flushDesignerPropertyDrafts'
import { flushDirtyObjectModules } from './objectModuleFlush'
import type { ObjectEditorState } from '../components/ObjectEditor/ObjectEditor'
import type { WorkspaceTab } from '../types/editor.types'

export type DesignerDraftState = { hydrated: boolean; dirty: boolean }

export function getDesignerDraftState(): DesignerDraftState {
  const { tree, configurationDirty } = useConfigStore.getState()
  const { tabs } = useWorkspaceStore.getState()
  return {
    hydrated: tree.length > 0,
    dirty: configurationDirty || tabs.some((t) => t.isDirty),
  }
}

export async function saveDesignerConfiguration(
  connectionId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  await flushDesignerPropertyDrafts()
  const tree = useConfigStore.getState().tree
  const { tabs, setTabDirty: mark } = useWorkspaceStore.getState()

  let flushedTabIds: string[] = []
  try {
    const res = await flushDirtyObjectModules({ connectionId, tabs, tree })
    flushedTabIds = res.flushedTabIds
  } catch {
    return { ok: false, error: 'Failed to save module code' }
  }

  try {
    flushedTabIds = [...flushedTabIds, ...(await flushDirtyPredefined(connectionId, tabs))]
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Failed to save predefined elements' }
  }

  try {
    const updateNodePatch = useConfigStore.getState().updateNodePatch
    const commonRes = await flushDirtyCommonModules({ tabs, tree, updateNodePatch })
    flushedTabIds = [...flushedTabIds, ...commonRes.flushedTabIds]
  } catch {
    return { ok: false, error: 'Failed to save common module code' }
  }

  try {
    const treeAfterFlush = useConfigStore.getState().tree
    const res = await configService.saveTree(connectionId, treeAfterFlush)
    const errs = [
      ...(res?.sync_results?.errors ?? []),
      ...(res?.sync_results?.config_migration?.errors ?? []),
    ].filter(Boolean)
    if (errs.length > 0) {
      return { ok: false, error: `Sync: ${errs.join('; ')}` }
    }

    useConfigStore.getState().markConfigurationClean()
    for (const t of useWorkspaceStore.getState().tabs) {
      if (
        t.isDirty &&
        (t.editorType === 'form' || t.editorType === 'layout' || t.editorType === 'report')
      ) {
        mark(t.id, false)
      }
    }
    for (const id of flushedTabIds) {
      mark(id, false)
    }
    return { ok: true }
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Failed to save configuration',
    }
  }
}

/** Unsaved predefined edits of "Open object schema" tabs (kept in the tab's editorState). */
async function flushDirtyPredefined(connectionId: string, tabs: WorkspaceTab[]): Promise<string[]> {
  const flushed: string[] = []
  for (const tab of tabs) {
    const state = tab.editorState as ObjectEditorState | undefined
    if (tab.editorType !== 'configObject' || !state?.predefinedDirty) continue
    try {
      const updated = await configObjectApi.updateObject(connectionId, tab.nodeId, {
        predefined: state.predefined ?? [],
      })
      useWorkspaceStore.getState().setTabEditorState(tab.id, {
        ...state,
        predefined: updated.predefined ?? state.predefined,
        predefinedDirty: false,
      })
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      throw new Error(`Failed to save predefined elements for ${tab.nodeId}: ${message}`)
    }
    flushed.push(tab.id)
  }
  return flushed
}
